import { Tabs, TabsList, TabsTrigger, TabCount } from '@/components/ui/tabs';

/*
 * TimeOfDayTabs — DESIGN_SYSTEM.md §2.2
 * Underline tabs: Morning / Afternoon / Evening with count chip per tab.
 * Tabs with count=0 are disabled.
 */

export type TimeOfDay = 'morning' | 'afternoon' | 'evening';

interface TimeOfDayTabsProps {
  value: TimeOfDay;
  onValueChange: (v: TimeOfDay) => void;
  counts: Record<TimeOfDay, number>;
  labels?: Record<TimeOfDay, string>;
  /** 'underline' (default) or 'segmented': full-width pill control. */
  variant?: 'underline' | 'segmented';
  className?: string;
}

const DEFAULT_LABELS: Record<TimeOfDay, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

export function TimeOfDayTabs({
  value,
  onValueChange,
  counts,
  labels = DEFAULT_LABELS,
  variant = 'underline',
  className,
}: TimeOfDayTabsProps) {
  const order: TimeOfDay[] = ['morning', 'afternoon', 'evening'];
  const segmented = variant === 'segmented';
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onValueChange(v as TimeOfDay)}
      variant={segmented ? 'pill' : 'underline'}
      className={className}
    >
      <TabsList className={segmented ? 'h-12 w-full gap-0.5 rounded-lg' : undefined}>
        {order.map((tod) => (
          <TabsTrigger
            key={tod}
            value={tod}
            disabled={counts[tod] === 0}
            className={segmented ? 'h-10 flex-1 rounded-md text-[14px] data-[state=active]:font-semibold' : undefined}
          >
            {labels[tod]}
            <TabCount>{counts[tod]}</TabCount>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** Bucket a Date into a TimeOfDay segment. Morning < 12, Afternoon < 17, else Evening. */
export function timeOfDay(d: Date): TimeOfDay {
  const h = d.getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
